<template>
  <section>
    <div class="d-flex align-items-center justify-content-between mb-1">
      <h1 class="title">{{ $t('entity.devices') }}</h1>
    </div>
    <filter-result
      v-if="filterResultData && isReady"
      :data="filterResultData"
      :headers="headers"
    ></filter-result>
    <base-table
      :no-data="!hasDevices"
      :headers="headers"
      :loading="loading"
      :sorting="sorting"
      @filter="filterData"
      @sorting="changeSorting"
      @isReady="isReadyTable"
    >
      <template #tbody>
        <device-table-row
          v-for="device in devices"
          :key="device.id"
          :device="device"
          @edit:device="startEditDevice"
          @reset:device="startResetDevice"
        ></device-table-row>
      </template>
    </base-table>
    <div class="mt-3">
      <paginator
        v-if="showPaginator"
        :total="totalCount"
        :per-page="perPage"
        v-model="currentPage"
        @change:count="numberOfElements"
      ></paginator>
    </div>
    <base-modal
      v-if="isHospitalAdmin"
      :is-show="isShowModalEdit"
      mode="apply"
      @close="closeModalEdit"
      @cancel="closeModalEdit"
      @confirm="saveDevice"
    >
      <template #header>{{ $t('src/pages/devices.9187') }}</template>
      <template #body v-if="editableDevice">
        <div class="filter-group text-left">
          <label class="filter-label">{{ $t('src/pages/devices.139') }}</label>
          <status-filter
            v-model="editableDevice.status"
            :status-list="statusList"
            :is-multiple="false"
          ></status-filter>
        </div>
      </template>
    </base-modal>
    <base-modal
      v-if="isNeuroAdmin"
      :is-show="isShowModalReset"
      mode="confirm"
      @close="closeModalReset"
      @cancel="closeModalReset"
      @confirm="resetDevice"
    >
      <template #header>{{ $t('src/pages/devices.2653') }}</template>
      <template #body v-if="editableDevice">{{ $t('src/pages/devices.1191') }}</template>
    </base-modal>
  </section>
</template>

<script>
import http from '@/modules/http.js'
import { stringify } from 'qs'
import { paginator } from '@/mixins/paginator.js'
import { getStatusListForFilter, deviceStatusList } from '@/modules/status-list.js'
import { userRights } from '@/mixins/user-rights.js'
import { deviceStatus } from '@/main.config'

export default {
  name: 'DevicesList',
  components: {
    BaseTable: () => import('@/base/BaseTable.vue'),
    DeviceTableRow: () => import('@/components/table-rows/DeviceTableRow.vue'),
    FilterResult: () => import('@/components/Filters/FilterResult.vue'),
    BaseModal: () => import('@/base/BaseModal.vue'),
    StatusFilter: () => import('@/components/Filters/StatusFilter.vue')
  },
  mixins: [paginator, userRights],
  data: () => ({
    loading: true,
    devices: [],
    status: 0,
    sorting: {
      by: 'serial',
      order: 'asc'
    },
    isShowModalEdit: false,
    isShowModalReset: false,
    editableDevice: null,
    isReady: false
  }),
  computed: {
    statusListForFilter() {
      return getStatusListForFilter('device')
    },
    statusList() {
      return deviceStatusList
    },
    sortParams() {
      const sortOrder = this.sorting.order === 'asc' ? 1 : -1
      if (this.sorting.by === 'serial') {
        return {
          serial_number: sortOrder
        }
      } else if (this.sorting.by === 'model') {
        return {
          model: sortOrder
        }
      }
      return {
        id: 1
      }
    },
    params() {
      let params = Object.assign({}, this.pageParams)

      params['$sort'] = this.sortParams

      if (this.status) {
        params['device.status'] = this.status
      } else if (Object.prototype.hasOwnProperty.call(params, 'device.status')) {
        delete params['device.status']
      }
      if (this.$store.getters.IS_SELECTED_HOSPITAL) {
        params['hospital_id'] = this.$store.getters.SELECTED_HOSPITAL
      }

      return params
    },
    filterResultData() {
      let data = {}

      if (this.status) {
        data[this.$t('general.status')] = this.statusListForFilter[this.status].title
      }
      return data
    },
    hasDevices() {
      return this.devices.length > 0
    },
    headers() {
      return {
        model: {
          title: this.$t('devicesHeader.model'),
          class: 'th-name',
          sorting: 'asc'
        },
        serial: {
          title: this.$t('devicesHeader.serialNumber'),
          sorting: 'asc'
        },
        status: {
          title: this.$t('general.status'),
          class: 'th-mid',
          filter: true,
          options: this.statusListForFilter,
          selected: this.status
        },
        course: {
          title: this.$t('devicesHeader.currentCourse')
        }
      }
    }
  },
  watch: {
    status() {
      this.loadData()
    }
  },
  mounted() {
    this.loadData()
  },
  methods: {
    loadData() {
      this.loading = true
      this.isReady = false
      http
        .get('/devices', {
          params: this.params,
          paramsSerializer: params =>
            stringify(params, {
              arrayFormat: 'indices',
              encodeValuesOnly: true
            })
        })
        .then(response => response.data)
        .then(devices => {
          this.totalCount = devices.total
          this.devices = devices.data
        })
        .catch(error => console.log(error))
        .finally(() => (this.loading = false))
    },
    filterData(event) {
      if (event.key === 'status') {
        this.status = +event.value
      }
    },
    changeSorting(sortingData) {
      this.sorting = sortingData
      this.loadData()
    },
    numberOfElements(number) {
      this.perPage = number
    },
    startEditDevice(device) {
      this.editableDevice = device
      this.isShowModalEdit = true
    },
    startResetDevice(device) {
      this.editableDevice = device
      this.isShowModalReset = true
    },
    closeModalEdit() {
      this.isShowModalEdit = false
      this.editableDevice = null
    },
    closeModalReset() {
      this.isShowModalReset = false
      this.editableDevice = null
    },
    saveDevice() {
      if (!this.editableDevice) {
        this.closeModalEdit()
        return
      }
      http
        .patch('/devices/' + this.editableDevice.id, {
          status: this.editableDevice.status
        })
        .then(response => response.data)
        .then(device => {
          const index = this.devices.findIndex(element => element.id === device.id)
          this.devices[index] = device
        })
        .catch(error => console.log(error))
        .finally(() => {
          this.closeModalEdit()
        })
    },
    resetDevice() {
      if (!this.editableDevice) {
        this.closeModalReset()
        return
      }
      http
        .patch('/devices/' + this.editableDevice.id, {
          status: deviceStatus.work,
          hospital_id: null
        })
        .then(response => response.data)
        .then(device => {
          this.devices = this.devices.filter(element => element.id !== device.id)
        })
        .catch(error => console.log(error))
        .finally(() => {
          this.closeModalReset()
        })
    },
    isReadyTable(value) {
      this.isReady = value
    }
  }
}
</script>
